import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router';
import type { Route } from './+types/quiz';
import type { ApiResult, QuestionPayload } from '@shared/types';
import '../styles/quiz.css';

export function meta(_: Route.MetaArgs) {
  return [
    { title: 'Take the Quiz — Tekavra Styling Room' },
    { name: 'description', content: 'Fifteen questions. A blended result. Find your real style.' },
  ];
}

const SESSION_KEY = 'styling-room-session-id';

const SLIDER_OPTIONS = [
  { value: 0, id: 'opt-pattern-0', label: 'Bare and empty' },
  { value: 25, id: 'opt-pattern-25', label: 'Clean with a little texture' },
  { value: 50, id: 'opt-pattern-50', label: 'Balanced pattern and calm' },
  { value: 75, id: 'opt-pattern-75', label: 'Layered and lived-in' },
  { value: 100, id: 'opt-pattern-100', label: 'More is more' },
];

export default function Quiz() {
  const navigate = useNavigate();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [question, setQuestion] = useState<QuestionPayload | 'done' | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [sliderValue, setSliderValue] = useState(50);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchNext = useCallback(async (sid: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/questions/next?sessionId=${sid}`);
      const json = (await res.json()) as ApiResult<QuestionPayload | { done: true }>;
      if (!json.ok) throw new Error(json.error);
      if ('done' in json.data) {
        setQuestion('done');
        navigate(`/results/${sid}`);
        return;
      }
      setQuestion(json.data);
      setSelected([]);
      setSliderValue(50);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong loading the next question.');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    let existing = window.localStorage.getItem(SESSION_KEY);
    (async () => {
      if (!existing) {
        const res = await fetch('/api/session', { method: 'POST' });
        const json = (await res.json()) as ApiResult<{ sessionId: string }>;
        if (!json.ok) {
          setError('Could not start the quiz. Refresh and try again.');
          setLoading(false);
          return;
        }
        existing = json.data.sessionId;
        window.localStorage.setItem(SESSION_KEY, existing);
      }
      setSessionId(existing);
      await fetchNext(existing);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function toggleOption(id: string, multi: boolean) {
    setSelected((prev) => {
      if (multi) {
        return prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      }
      return [id];
    });
  }

  async function handleContinue() {
    if (!sessionId || question === 'done' || !question) return;
    setSubmitting(true);
    const optionIds = question.type === 'slider' ? [SLIDER_OPTIONS.find((o) => o.value === sliderValue)!.id] : selected;
    try {
      await fetch('/api/answer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, questionId: question.id, optionIds }),
      });
      await fetchNext(sessionId);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading && !question) {
    return (
      <main className="quiz-shell">
        <p className="eyebrow">Loading your quiz…</p>
      </main>
    );
  }

  if (error) {
    return (
      <main className="quiz-shell">
        <p className="form-error">{error}</p>
        <button className="ui-btn btn-secondary" onClick={() => window.location.reload()}>
          Try again
        </button>
      </main>
    );
  }

  if (!question || question === 'done') {
    return (
      <main className="quiz-shell">
        <p className="eyebrow">Wrapping up your result…</p>
      </main>
    );
  }

  const isMulti = question.type === 'image-multiselect' || question.type === 'text-multiselect';
  const canContinue = question.type === 'slider' ? true : selected.length > 0;
  const showTiles = question.type === 'image-multiselect' || question.type === 'image-single' || question.type === 'forced-choice';

  return (
    <main className="quiz-shell">
      <div className="quiz-progress">
        <span className="eyebrow">
          {String(question.index).padStart(2, '0')} / {String(question.total).padStart(2, '0')}
        </span>
        <div className="quiz-progress-track">
          <div className="quiz-progress-fill" style={{ width: `${(question.index / question.total) * 100}%` }} />
        </div>
      </div>

      <h1 className="quiz-prompt" key={question.id}>
        {question.prompt}
      </h1>

      {showTiles && (
        <div className="quiz-tile-grid" key={question.id}>
          {question.options.map((opt, i) => (
            <button
              key={opt.id}
              type="button"
              className={`quiz-tile ${selected.includes(opt.id) ? 'is-selected' : ''}`}
              style={{ animationDelay: `${i * 40}ms` }}
              onClick={() => toggleOption(opt.id, isMulti)}
            >
              {opt.imageUrl ? (
                <img src={opt.imageUrl} alt="" className="quiz-tile-img" />
              ) : (
                <div className="quiz-tile-img quiz-tile-img--empty" />
              )}
              <span className="quiz-tile-label">{opt.label}</span>
              {selected.includes(opt.id) && <span className="quiz-tile-check">✓</span>}
            </button>
          ))}
        </div>
      )}

      {question.type === 'text-multiselect' && (
        <div className="quiz-chip-row" key={question.id}>
          {question.options.map((opt, i) => (
            <button
              key={opt.id}
              type="button"
              className={`quiz-chip ${selected.includes(opt.id) ? 'is-selected' : ''}`}
              style={{ animationDelay: `${i * 30}ms` }}
              onClick={() => toggleOption(opt.id, true)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {question.type === 'slider' && (
        <div className="quiz-slider-wrap">
          <input
            type="range"
            min={0}
            max={100}
            step={25}
            value={sliderValue}
            onChange={(e) => setSliderValue(Number(e.target.value))}
            className="quiz-slider"
          />
          <p className="quiz-slider-label">{SLIDER_OPTIONS.find((o) => o.value === sliderValue)?.label}</p>
        </div>
      )}

      <button className="ui-btn btn-primary btn-lg quiz-continue" disabled={!canContinue || submitting} onClick={handleContinue}>
        {submitting ? 'Saving…' : question.index === question.total ? 'See my result' : 'Continue'}
      </button>
    </main>
  );
}
